import { userObj, accountObj, tempDataObj } from '../store'
export { userObj } from '../store'

const levels = [
  'emailrecipient',
  'reporter',
  'accountuser',
  'accountadmin',
  'agencyuser',
  'agencyadmin',
  'superadmin',
  'developer',
]

/**
 *  Will return a boolean based on the list of users allowed to see it
 *  and the logged in users type
 *  @param   {String} type      - show/hide
 *  @param   {String} userTypes - comma separated
 *  @param   {Object=} user - optional user object
 *  @returns {Boolean}
 */
export function display(type, userTypes, user) {
  if (typeof type !== 'string' || typeof userTypes !== 'string') return false
  const list = userTypes.split(',')
  const check = list.indexOf(userObj(user).type) !== -1
  const res = type === 'show' ? check : !check
  return res
}

/**
 * @param  {String} level level to check against
 * @return {Boolean}       True if higher or equal to the given level, false
 * if lower than the given level
 * @param   {Object=} user - optional user object
 * @description Check if the current signed in user is above or equal to a
 * certain user level
 */
export function userLevelIsHigerThan(level, user) {
  if (!level) return false

  level = level.toLowerCase()

  const userLevel = userObj(user).type

  // Check request user level exists in the levels array
  if (levels.indexOf(level) === -1) return false

  // Get the postions level then do a a check!
  const requestPostion = levels.indexOf(level)
  const userPostion = levels.indexOf(userLevel)
  const res = userPostion >= requestPostion
  return res
}

/**
 * Check if level A is above or equal to level B
 * @param  {String} levelA level to check against levelB
 * @param  {String} levelB level to check against levelB
 * @return {Boolean}       True if leval A is higher or equal to level B
 */
export function levelAHigerThanB(levelA, levelB) {
  if (!levelA || !levelB) return false

  levelA = levelA.toLowerCase()
  levelB = levelB.toLowerCase()

  // Check request user level exists in the levels array
  if (levels.indexOf(levelA) === -1) return false

  // Get the postions level then do a a check!
  const requestPostion = levels.indexOf(levelA)
  const levelBPos = levels.indexOf(levelB)

  return levelBPos <= requestPostion
}

/**
 *  Denies access to users for certain routes this is based on the users
 *  userlevel
 *  @param   {String} userTypes - comma separated
 *  @param   {Object=} user - optional user object
 *  @returns {Boolean}
 */
export function denyAccess(userTypes, user) {
  // If no user types given return false as all levels will be allowed in
  if (typeof userTypes !== 'string') return false

  // Else we need to check the user types that are allowed into the route
  // we need to split and trim the string so we can do an index of on an
  // array
  const list = userTypes.split(',').map((type) => type.trim())

  return list.indexOf(userObj(user).type) === -1
}

/**
 * @param   {Object=} user - optional user object
 * @returns {boolean}
 * @description Returns true if the logged in user is a developer, false otherwise
 */
export function isDeveloper(user) {
  return userObj(user).groups.indexOf('developer') !== -1
}

/**
 *  @param  {String} group
 *  @param  {Object=} user - optional user object
 *  @return {Boolean}
 *  @description Checks if the user has a specific group, unless they are a developer
 *  or a superadmin
 */
export function hasGroup(group, user) {
  const sess = userObj(user).groups
  if (sess.indexOf('developer') !== -1) return true
  if (sess.indexOf('superadmin') !== -1) return true
  return sess.indexOf(group) !== -1
}

/**
 * @param {Object} lead
 * @param {Function=} callback - optional
 * @param {Object=} user - optional user object
 * @description Checks the if the user is allowed to view a lead that, this checks if they
 * are limited to thier own leads, and if they are it will check if the lead is
 * assigned to them.
 */
export function allowedToViewLead(lead, callback, user) {
  const session = userObj(user)
  let isAllowed = true

  if (session.viewAssignedOnly) {
    if (!lead.assignee || !lead.assignee._id) isAllowed = false
    else isAllowed = lead.assignee._id === session._id
  }

  if (typeof callback === 'function') {
    return callback(isAllowed)
  }

  return isAllowed
}

/**
 * Checks the if the user is allowed to view a lead that, this checks if they
 * are limited to thier own leads, and if they are it will check if the lead is
 * assigned to them.
 *
 * @param {Object} lead
 * @param {Function=} callback - optional
 * @param {Object=} user - optional user object
 */
export function allowedToViewContact(contact, callback, user) {
  const session = userObj(user)
  let isAllowed = true

  if (session.viewAssignedContactsOnly) {
    if (!contact.owner) isAllowed = false
    else isAllowed = contact.owner === session._id
  }

  if (typeof callback === 'function') {
    return callback(isAllowed)
  }

  return isAllowed
}

/**
 * @param {Boolean} lock - determines whether to lock the user out
 * @description checks if the account is their own
 * @returns {Boolean}
 */
export function isOwnAccount(lock) {
  const { account, type } = userObj()
  const { tempAccount } = tempDataObj()
  const admin = ['developer', 'superadmin'].indexOf(type) !== -1
  const owner = tempAccount === account._id

  const res =
    lock && !owner // if not the owner and lock is true
      ? false // lock the user out
      : owner // else we check if they are the owner
      ? true // if they are let them in
      : admin // else we need to check if they are an admin
      ? true // if they are let them in
      : false // else lock them out

  return res
}

/**
 * Returns true if the drilled into the their own account, false otherwise
 */
export function isTrueOwnAccount(user, account) {
  const session = userObj(user)
  const currentAccount = accountObj(account)
  return currentAccount.id === session.account.id
}

/**
 * Find the user level on the user account
 * @params user User object
 * @params acccountId Account ID
 */
export function findUserLevelOnAccount(user, accountId) {
  try {
    if (!user) return undefined
    if (!user.accounts) return undefined

    for (let i = 0; i < user.accounts.length; i++) {
      if (user.accounts[i].account._id === accountId) {
        return user.accounts[i].userLevel
      }
    }

    return undefined
  } catch (ex) {
    console.error('ERROR findUserLevelOnAccount', ex)
    return undefined
  }
}

/**
 * Find the current active account on a given user, this is so it's possible
 * to get account specifc user settings.
 * @param {Object} user User to find the current active account on
 */
export function getUserActiveAccount(user) {
  if (!user) return

  const activeAcc = accountObj()
  let account

  for (let index = 0; index < user.accounts.length; index++) {
    if (user.accounts[index].account._id === activeAcc._id) {
      account = user.accounts[index]
    }

    // Am not testing this. It seems pointless, as the accounts array cannot contain
    // strings and the account data in the drilldowns will always be an object, not sure
    // why this exists, but leaving it incase anything breaks
    /* istanbul ignore if */
    if (user.accounts[index].account === activeAcc) {
      account = user.accounts[index]
    }
  }

  return account
}
